import { IReportMeasurementGroup } from "Data";

// TestStep = "#1R5;100;150", "#2D4", "#3F10;56"
// #1 = step number (number is only used in session and added by TestCenterModel)
// R = random target
// 5 = number of shots to target
// 100;150 = Range
// Range and distance is in double CultureInfo.InvariantCulture

/*
    Definition:
        RegEx: "\d+(\?|\*)<targetdef>(,<targetdef>)*"
        RegEx<targetded>: "D(\d+)?(;[1-3])?|F(\d+)?;\d+|R(\d+)?;[0-9]*\.?[0-9]*;[0-9]*\.?[0-9]*|S(\d+)?;\d{1,2}"
	
        1* = Do not repeat targets
        2* = Repeat targets twice
        1? = Targets in random order
        2? = Repeat targets in random order twice.
	
        D = Drives, Eg: "D" is zero or more drives. "D2" is two drives. "D7" is seven drives
        F = Fixed target (in meter), Eg: "F;40" is zero or more at 40m target. "F2;50.7" is two at 50.7m target.
        R = Random target (in meter) Eg: "R;40;60" is zero or more random between 40m and 60m. "F8;50.5;70" is eight random between 50.5m and 70m
        S = Standard Target (in 10x meter.) Eg: "S;7" is zero or more at (60m<x<=70m). "S6;18 is 6 shots at (170m<x<=180m)
                S;1 = 5m/10y 
                S;2 = 15m/20y 
                S;3 = 25m/30y 
                S;4 = 35m/40y 
                S;5 = 45m/50y 
                S;6 = 55m/60y 
                S;7 = 65m/70y
                S;8 = 75m/80y
                S;9 = 85m/90y
                S;10 = 95m/100y
                S;11 = 105m/120y
                S;12 = 115m/130y
                S;13 = 125m/140y
                S;14 = 135m/150y
                S;15 = 145m/160y
                S;16 = 155m/170y
                S;17 = 165m/180y
                S;18 = 175m/190y
                S;19 = 185m/200y
                S;20 = 195m/210y
                D =   Drives (normal)
                D;1 = Junior drive Level 1 (very easy)
                D;2 = Junior drive Level 2 (easy)
                D;2 = Junior drive Level 3 (harder)
	
                The Standard Combine Test is: "2*S3;6,S3;7,S3;8,S3;9,S3;10,S3;11,S3;13,S3;15,S3;17,D3"

	
*/

export class TargetsHelper {
  static targetInUnits(group: IReportMeasurementGroup, unit: string): string {
    
    if (unit === "e" || unit === "m") {
      switch (group.targetDefinition) {
        case "S;1":
          return "5";
        case "S;2":
          return "15";
        case "S;3":
          return "25";
        case "S;4":
          return "35";
        case "S;5":
          return "45";
        case "S;6":
          return "55";
        case "S;7":
          return "65";
        case "S;8":
          return "75";
        case "S;9":
          return "85";
        case "S;10":
          return "95";
        case "S;11":
          return "105";
        case "S;12":
          return "115";
        case "S;13":
          return "125";
        case "S;14":
          return "135";
        case "S;15":
          return "145";
        case "S;16":
          return "155";
        case "S;17":
          return "165";
        case "S;18":
          return "175";
        case "S;19":
          return "185";
        case "S;20":
          return "195";
        case "D":
          return "Drive";
        case "D;1":
          return "Junior L1"; // "Junior drive Level 1 (very easy)";
        case "D;2":
          return "Junior L2"; // "Junior drive Level 2 (easy)";
        case "D;3":
          return "Junior L3"; // "Junior drive Level 3 (harder)";
        default:
          return this.findTarget(group, unit);
      }
    }

    if (unit === "u" || unit === "j"){
      switch (group.targetDefinition) {
        case "S;1":
          return "10";
        case "S;2":
          return "20";
        case "S;3":
          return "30";
        case "S;4":
          return "40";
        case "S;5":
          return "50";
        case "S;6":
          return "60";
        case "S;7":
          return "70";
        case "S;8":
          return "80";
        case "S;9":
          return "90";
        case "S;10":
          return "100";
        case "S;11":
          return "120";
        case "S;12":
          return "130";
        case "S;13":
          return "140";
        case "S;14":
          return "150";
        case "S;15":
          return "160";
        case "S;16":
          return "170";
        case "S;17":
          return "180";
        case "S;18":
          return "190";
        case "S;19":
          return "200";
        case "S;20":
          return "210";
        case "D":
          return "Drive";
        case "D;1":
          return "Junior L1"; // "Junior drive Level 1 (very easy)";
        case "D;2":
          return "Junior L2"; // "Junior drive Level 2 (easy)";
        case "D;3":
          return "Junior L3"; //"Junior drive Level 3 (harder)";
        default:
          return this.findTarget(group, unit);
      }
    }
    return this.findTarget(group, unit);
  }

  static findTarget(group: IReportMeasurementGroup, unit: string): string {
    if (this.checkFirstLetter(group.targetDefinition ?? "") === "D") {
      return "Drive";
    } else if (
      this.checkFirstLetter(group.targetDefinition ?? "") === "F" ||
      this.checkFirstLetter(group.targetDefinition ?? "") === "R"
    ) {
      return this.findFixedTargetOrRange(group, unit);
    } else {
      return "Undefined";
    }
  }

  static checkFirstLetter(value: string): string {
    return value.charAt(0);
  }

  static findFixedTargetOrRange(
    group: IReportMeasurementGroup,
    unit: string
  ): string {
    let valueComponents = (group.targetDefinition && group.targetDefinition.split(";")) ?? "";

    if (valueComponents.length === 2) {
      return this.convertToYardsIfNeeded(valueComponents[1], unit).toString();
    } else if (valueComponents.length === 3) {
      return (
        this.convertToYardsIfNeeded(valueComponents[1], unit).toString() +
        "-" +
        this.convertToYardsIfNeeded(valueComponents[2], unit).toString()
      );
    } else {
      return "Undefined";
    }
  }

  static convertToYardsIfNeeded(val: string, unit: string): number {
    //values should be rounded with no decimals.
    var value = parseFloat(val);
    if (unit === "u" || unit === "j") {
      return Math.round(value * (1 / 0.9144));
    } 
    return Math.round(value);    
  }

  static findTargetValuesForSorting(target: string): number | null{
    let valueComponents = target.split(";");
    let driveAndJuniorTargets = "D" || "D;1" || "D;2" || "D;3";
    if ((valueComponents.length === 2 || valueComponents.length === 3) && target !== driveAndJuniorTargets) {
      return parseFloat(valueComponents[1]);
    } else {
      return null;
    }
  }
}
